import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { alpha, useTheme } from "@mui/material/styles";
import ConfirmBusDialog from "../dialogs/ConfirmBusDialog";
import { buildMemberDetail } from "../../../shared utils/personDetail";

function fmtTime(t) {
  if (!t) return null;
  return String(t).slice(0, 5);
}

function normStatus(s) {
  return String(s || "").trim().toLowerCase();
}

export default function JourneyCard({
  journey,
  onEdit,
  onDelete,
  onSavePassengers, // async (journeyId, nextPassengers) => void
  onConfirmJourney, // async (journey, { contactName, contactNumber }) => void
}) {
  const status = normStatus(journey.status || "Unknown");
  // A ticket block is a transport group of type TICKET — "a new kind of bus".
  // Same confirm/passenger machinery, just relabelled for tickets.
  const isTicketBlock =
    String(journey?.journeyType || "").trim().toUpperCase() === "TICKET";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusOpen, setConfirmBusOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savingPassengers, setSavingPassengers] = useState(false);

  const isConfirmed = status === "confirmed" || Boolean(journey.confirmedAt);

  const statusChipColor =
    status === "confirmed"
      ? "success"
      : status === "booked"
      ? "warning"
      : status === "planned"
      ? "error"
      : "default";

  const theme = useTheme();

  const sxForStatus = useMemo(() => {
    if (status === "confirmed") {
      // Match the "ready" green used on activity-group cards.
      return {
        borderColor: theme.palette.success.main,
        bgcolor: alpha(theme.palette.success.main, 0.1),
      };
    }
    if (status === "booked") {
      return {
        borderColor: theme.palette.warning.main,
        bgcolor: alpha(theme.palette.warning.main, 0.12),
      };
    }
    if (status === "planned") {
      return {
        borderColor: theme.palette.error.main,
        bgcolor: alpha(theme.palette.error.main, 0.12),
      };
    }
    if (status === "complete" || status === "completed") {
      return {
        borderColor: theme.palette.grey[400],
        bgcolor: alpha(theme.palette.grey[500], 0.08),
      };
    }
    return {
      borderColor: theme.palette.divider,
      bgcolor: "background.paper",
    };
  }, [status, theme]);

  const routeLabel = useMemo(() => {
    const stops = Array.isArray(journey.stops)
      ? journey.stops.slice().sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
      : [];
    if (stops.length >= 2) return stops.map((s) => s.location).filter(Boolean).join(" → ");
    return null;
  }, [journey]);

  const timeLabel = useMemo(() => {
    const dep = fmtTime(journey.departTime);
    const arr = fmtTime(journey.arrivalTime);
    if (dep && arr) return `${dep} → ${arr}`;
    if (dep) return `Dep ${dep}`;
    if (arr) return `Arr ${arr}`;
    return null;
  }, [journey.departTime, journey.arrivalTime]);

  const passengers = useMemo(() => {
    const list = Array.isArray(journey.passengers) ? journey.passengers : [];
    return list.slice().sort((a, b) => {
      const aIsAdult = String(a?.personType).toUpperCase() === "ADULT";
      const bIsAdult = String(b?.personType).toUpperCase() === "ADULT";
      if (aIsAdult !== bIsAdult) return aIsAdult ? -1 : 1;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  }, [journey.passengers]);

  const counts = useMemo(() => {
    const adults = passengers.filter((p) => String(p?.personType).toUpperCase() === "ADULT").length;
    const students = passengers.length - adults;
    return { adults, students, total: passengers.length };
  }, [passengers]);

  const handleRequestDelete = () => setConfirmOpen(true);

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    await onDelete?.(journey);
  };

  const paxKey = (p) => `${String(p?.personType).toUpperCase()}:${p?.id}`;

  const handleRemovePassenger = async (passengerToRemove) => {
    if (!onSavePassengers || savingPassengers) return;

    const removeKey = paxKey(passengerToRemove);

    const nextPassengers = passengers
      .filter((p) => paxKey(p) !== removeKey)
      .map((p) => ({
        personType: p.personType,
        id: p.id,
        name: p.name,
      }));

    setSavingPassengers(true);
    try {
      await onSavePassengers(journey.id, nextPassengers);
    } finally {
      setSavingPassengers(false);
    }
  };

  const handleClearBus = async () => {
    if (!onSavePassengers || savingPassengers || passengers.length === 0) return;

    setSavingPassengers(true);
    try {
      await onSavePassengers(journey.id, []);
    } finally {
      setSavingPassengers(false);
    }
  };

  return (
    <>
      <Card variant="outlined" sx={{ height: "100%", ...sxForStatus }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography fontWeight={800}>{journey.groupName || "Journey"}</Typography>
              {isTicketBlock && (
                <Chip size="small" label="Tickets" color="info" variant="outlined" />
              )}
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {onConfirmJourney && !isConfirmed && (
                <Button
                  size="small"
                  color="success"
                  variant="outlined"
                  startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                  onClick={() => setConfirmBusOpen(true)}
                  aria-label={isTicketBlock ? "Confirm tickets" : "Confirm bus"}
                >
                  {isTicketBlock ? "Confirm tickets" : "Confirm"}
                </Button>
              )}
              {onEdit && !isTicketBlock && (
                <IconButton size="small" onClick={onEdit} aria-label="Edit bus">
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  size="small"
                  onClick={handleRequestDelete}
                  aria-label={isTicketBlock ? "Delete ticket block" : "Delete bus"}
                  disabled={Boolean(journey.confirmedAt)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              <Chip
                size="small"
                label={journey.status || "—"}
                color={statusChipColor}
                variant={status === "confirmed" ? "filled" : "outlined"}
              />
            </Stack>
          </Stack>

          {routeLabel && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {routeLabel}
            </Typography>
          )}

          {timeLabel && (
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              {timeLabel}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="body2">
            <b>Capacity:</b> {journey.capacity ?? "—"}
          </Typography>

          <Accordion
            elevation={0}
            disableGutters
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            sx={{ mt: 1, "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 0, minHeight: 32, "& .MuiAccordionSummary-content": { my: 0 } }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                <Typography variant="body2" fontWeight={700}>
                  {isTicketBlock ? "Tickets" : "Passengers"}
                </Typography>
                <Chip size="small" label={counts.total} />
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {counts.adults} adults • {counts.students} students
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              {expanded && onSavePassengers && (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={passengers.length === 0 || savingPassengers}
                    onClick={handleClearBus}
                  >
                    {savingPassengers ? "Saving…" : isTicketBlock ? "Clear" : "Clear bus"}
                  </Button>
                </Stack>
              )}

              {passengers.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  No passengers assigned.
                </Typography>
              ) : (
                <List dense disablePadding sx={{ mt: 0.25 }}>
                  {passengers.map((p) => {
                    const isAdult = String(p?.personType).toUpperCase() === "ADULT";
                    const name = String(p?.name ?? "").trim();
                    const label = name || (isAdult ? `Adult #${p?.id}` : `Student ${p?.id}`);
                    const detail = buildMemberDetail(p, isAdult);

                    return (
                      <ListItem
                        key={`${p.personType}-${p.id}`}
                        disableGutters
                        sx={{ py: 0, px: 0 }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                          {expanded && onSavePassengers ? (
                            <IconButton
                              size="small"
                              aria-label="Remove passenger"
                              onClick={() => handleRemovePassenger(p)}
                              color="error"
                              disabled={savingPassengers}
                            >
                              <HighlightOffIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <span style={{ width: 34 }} />
                          )}

                          <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                            <Box component="span" fontWeight={isAdult ? 800 : 400}>
                              {label}
                            </Box>
                            {detail && (
                              <Box
                                component="span"
                                sx={{ opacity: 0.7 }}
                                title={p.bookingGroupName || undefined}
                              >
                                {` — ${detail}`}
                              </Box>
                            )}
                          </Typography>
                        </Stack>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </AccordionDetails>
          </Accordion>

          {journey.provider && (
            <Typography variant="body2">
              <b>{isTicketBlock ? "Supplier" : "Provider"}:</b> {journey.provider}
            </Typography>
          )}

          {(journey.confirmedBy || journey.confirmedAt) && (
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              <b>Confirmed:</b> {journey.confirmedBy || "—"}{" "}
              {journey.confirmedAt ? `• ${String(journey.confirmedAt).replace("T", " ").slice(0, 16)}` : ""}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete transport group?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will remove passengers from the group and unschedule it.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
            {journey.groupName || `Group #${journey.id}`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmBusDialog
        open={confirmBusOpen}
        onClose={() => setConfirmBusOpen(false)}
        journey={journey}
        onConfirm={(contact) => onConfirmJourney(journey, contact)}
        {...(isTicketBlock
          ? {
              title: "Confirm tickets",
              description: `Confirm tickets are secured for everyone in${
                journey?.groupName ? ` “${journey.groupName}”` : " this block"
              }.`,
              confirmLabel: "Confirm tickets",
              nameLabel: "Supplier / booking ref",
              numberLabel: "Confirmed by",
            }
          : {})}
      />
    </>
  );
}